import {Link} from 'react-router';
import React from 'react';
import {
    useToggleTrainingProblemMutation,
    useGetTrainingProblemQuery,
} from '@api/problems';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faInfoCircle, faRepeat} from '@fortawesome/free-solid-svg-icons';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ProblemDestinationToggle} from './ProblemDestinationToggle';

interface TrainingProblemToggleProps {
    problemId: number;
}

const TrainingProblemToggle: React.FC<TrainingProblemToggleProps> = ({
    problemId,
}) => {
    const {t} = useTranslation();
    const {
        data: trainingProblem,
        isFetching: isFetchingQuery,
        isError,
        refetch,
    } = useGetTrainingProblemQuery(problemId);
    const [toggleTrainingProblem, {isLoading: isLoadingMutation}] =
        useToggleTrainingProblemMutation();

    const isInTraining = trainingProblem != null;
    const isLoading = isFetchingQuery || isLoadingMutation;

    const handleToggle = async () => {
        try {
            await toggleTrainingProblem(problemId).unwrap();
            // The query will automatically refetch due to invalidatesTags
        } catch (error) {
            toast.error(
                (error as {data?: {message?: string}})?.data?.message ??
                    t('problem_collections.add_to.training_save_error')
            );
        }
    };

    return (
        <ProblemDestinationToggle
            title={t('problem_collections.add_to.training')}
            description={
                isError && !isLoading
                    ? t('problem_collections.add_to.training_load_error')
                    : undefined
            }
            actionLabel={
                isError && !isLoading
                    ? t('training.retry')
                    : t(
                          isInTraining
                              ? 'problem_collections.add_to.remove_training'
                              : 'problem_collections.add_to.add_training'
                      )
            }
            icon={<FontAwesomeIcon icon={faRepeat} />}
            selected={isInTraining}
            busy={isLoading}
            onClick={() => {
                if (isError) {
                    void refetch();
                } else {
                    void handleToggle();
                }
            }}
            info={
                <Link
                    to="/article/training"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="problem-destination-info"
                    aria-label={t('problem_collections.add_to.training_info')}
                    title={t('problem_collections.add_to.training_info')}>
                    <FontAwesomeIcon icon={faInfoCircle} />
                </Link>
            }
        />
    );
};

export default TrainingProblemToggle;
